import { v4 as uuidv4 } from "uuid";
import { JsonType } from "@/hooks/useFetch";
import { normalizeDataV1, V1FileData } from "./datasetVersions/v1";
import { normalizeDataV2, V2FileData } from "./datasetVersions/v2";

type Cell = string;
type Row = Cell[];
export type Sheet = Row[];

export type SheetWithIndexes = {
  id: string;
  cells: Row;
}[];

export function numberToLetters(n: number): string {
  let result = "";

  while (n > 0) {
    const remainder = (n - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    n = Math.floor((n - 1) / 26);
  }

  return result;
}

export function lettersToNumber(s: string): number {
  let result = 0;

  for (let i = 0; i < s.length; i++) {
    const char = s.charCodeAt(i) - 64;
    result = result * 26 + char;
  }

  return result;
}

export function sheetWithIndexesToSheet(withRows: SheetWithIndexes): Sheet {
  return withRows.map((row) => row.cells);
}

export function genIndexesOnSheet(sheet: Sheet): SheetWithIndexes {
  const data = sheet.map((row) => {
    return {
      id: uuidv4(),
      cells: row as Row,
    };
  });
  return data;
}

export type NormalizedDataPoint = {
  address: string;
  date: Date;
  dateString: string;
  price: number;
  priceString: string;
  id: string;
  outlier?: boolean;
  comp?: boolean;
};

export function castDataToVersion(data: JsonType, version: "v1"): V1FileData;
export function castDataToVersion(data: JsonType, version: "v2"): V2FileData;
export function castDataToVersion(data: JsonType, version: "v1" | "v2"): V1FileData | V2FileData {
  switch (version) {
    case "v1":
      return data as unknown as V1FileData;
    case "v2":
      return data as unknown as V2FileData;
  }
}

export function detectDatasetVersion(data: JsonType) {
  if (!("version" in data)) {
    return "v1";
  }

  switch (data.version) {
    case "v2":
      return "v2";
  }
}

export const columnIndexes = {
  v1: {
    address: 0,
    date: 1,
    price: 2,
  },
  v2: {
    address: 0,
    address2: 1,
    date: 2,
    price: 3,
  },
};

export type Comp = { id: string };

export function normalizeDataBasedOnVersion(data: JsonType, outliers?: string[], comps?: Comp[]) {
  const version = detectDatasetVersion(data);
  switch (version) {
    case "v1": {
      const castedData = castDataToVersion(data, version);
      return normalizeDataV1(castedData, outliers, comps);
    }
    case "v2": {
      const castedData = castDataToVersion(data, version);
      return normalizeDataV2(castedData.data, outliers, comps);
    }
    default: {
      return [];
    }
  }
}

export type PreparedDatasetType = {
  sheet: Sheet;
  amtColumnIdx: number;
  dateColumnIdx: number;
  nameColumnIdx: number;
  containsHeader: boolean;
};

const config: { [key: string]: number } = {
  A: 0,
  B: 1,
  C: 2,
  D: 3,
  E: 4,
};

export function lookupColByIdx(val: string): number {
  if (val) {
    return config[val];
  }

  throw new Error("unknown val");
}

export function deriveDateIndex(val: string): number {
  if (val) {
    return config[val];
  }

  throw new Error("unknown val");
}

export function emptySheet(rows: number, cols: number): Sheet {
  const array1 = [...new Array(cols)];
  const array2 = [...new Array(rows)];
  return array1.map((_col) => {
    return array2.map((_row) => {
      return "";
    });
  });
}

export const threeByTwoBlankSheet = [[{ value: "" }, { value: "" }, { value: "" }]];

export function isDatasetEmpty(data: Sheet | undefined): boolean {
  if (!data) return true;
  if (data.length > 1) return false;
  if (data.length == 0) return true;

  if (data[0] && data[0][0] == "") return true;

  return false;
}
